import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { mq } from '@/theme/scalingTokens';

export const useEventDataGridStyles = makeStyles({
  root: {
    width: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 16rem)',
    minHeight: '10rem',
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    boxSizing: 'border-box',
    position: 'relative',
    // Firefox: thin scrollbar, fully transparent by default.
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    // WebKit / Chromium / Edge: invisible track + transparent thumb that fades in.
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'transparent',
      borderRadius: tokens.borderRadiusMedium,
      transitionProperty: 'background-color',
      transitionDuration: '0.6s',
      transitionTimingFunction: 'ease-out',
    },
    // Show on active scroll (data-scrolling toggled by JS) and on hover.
    '&[data-scrolling="true"]::-webkit-scrollbar-thumb, &:hover::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colorNeutralStroke1Hover,
    },
    '&[data-scrolling="true"], &:hover': {
      scrollbarColor: `${tokens.colorNeutralStroke1Hover} transparent`,
    },
    [mq.highZoom]: {
      maxHeight: 'none',
    },
  },

  fetchingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
  },

  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
  },

  gridContainer: {
    width: '100%',
    minWidth: '700px',
    '& [role="grid"]': {
      width: '100%',
    },
    '& [role="row"]': {
      width: '100%',
    },
    '& table': {
      width: '100%',
    },
  },

  headerCell: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
  },

  stickyHeader: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: tokens.spacingVerticalS,
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
  },

  row: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorBrandForeground1,
      outlineOffset: '-2px',
    },
  },

  rowSelected: {
    backgroundColor: tokens.colorSubtleBackgroundSelected,
    '&:hover': {
      backgroundColor: tokens.colorSubtleBackgroundSelected,
    },
  },

  cell: {
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    overflow: 'hidden',
  },

  primaryText: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  secondaryText: {
    fontSize: fontScale.caption,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  twoLineCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    ...shorthands.overflow('hidden'),
  },

  personaCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    ...shorthands.gap('0.375rem'),
    ...shorthands.overflow('hidden'),
  },

  personaName: {
    fontSize: fontScale.body,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  statusBadge: {
    display: 'inline-block',
    alignItems: 'center',
    maxWidth: '100%',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },

  roomCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    ...shorthands.overflow('hidden'),
  },

  roomChip: {
    marginInlineStart: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },

  skeletonRow: {
    display: 'flex',
    minHeight: '45px',
    flexDirection: 'row',
    alignItems: 'center',
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.gap(tokens.spacingHorizontalM),
  },

  skeletonCell: {
    ...shorthands.flex(1),
  },

  bottomShimmerRows: {
    display: 'flex',
    flexDirection: 'column',
  },

  batchErrorRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    ...shorthands.borderTop(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorStatusDangerForeground1,
    fontSize: fontScale.body,
  },

  visuallyHidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    ...shorthands.padding('0'),
    ...shorthands.margin('-1px'),
    ...shorthands.overflow('hidden'),
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    ...shorthands.borderWidth('0'),
  },

  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
    ...shorthands.gap(tokens.spacingVerticalM),
    minHeight: '12.5rem',
  },

  errorText: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground3,
  },

  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
    minHeight: '200px',
  },

  emptyText: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground3,
  },

  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding(tokens.spacingVerticalM, '0'),
  },
});
